/**
 * Pendo root — wires `@revalize/pendo`'s `PendoProvider` with this app's
 * identity mapping.
 *
 * The library owns the lifecycle (deferral, init, re-identify on visitor
 * change, `updateOptions` forwarding, retry on failure). This component owns
 * only the two things it cannot know: which provider implementation this
 * environment should use, and how an AQ-Cloud2 user maps onto a Pendo
 * visitor/account.
 *
 * Identity comes from `useApi().user` — the repo's documented source of truth
 * for app user data — rather than raw OIDC claims, so there is no
 * `custom:tenantId` claim-name guessing of the kind AQ-Webcatalog needs.
 */

import { useMemo, type ReactElement, type ReactNode } from 'react'
import { PendoProvider, type PendoAccount, type PendoVisitor } from '@revalize/pendo/frontend'
import { useApi } from '@/hooks/useAppApi'
import { usePermissions } from '@/hooks/usePermissions'
import { ConfigService } from '@/services/ConfigService'
import { createAppPendoProvider } from '@/lib/pendo'

/**
 * Delay before the agent script is requested. Long enough that the initial
 * route's own data fetches win the connection, short enough that a user who
 * lands and immediately acts is still captured. (Also the library's own
 * default; stated explicitly so the intent survives a library default change.)
 */
const DEFER_MS = 2000

export const PendoRoot = ({ children }: { children: ReactNode }): ReactElement => {
  const { user, isAuthenticated } = useApi()
  const { permissions, roleNames } = usePermissions()

  const apiKey = ConfigService.getPendoConfig()?.apiKey
  const isDevAuthMode = ConfigService.isDevAuthMode()

  // Construct the provider once per environment shape. The library keys its
  // internal initializer on provider identity, so returning a new instance on
  // every render would remount the initializer and re-run initialization.
  const provider = useMemo(
    () => createAppPendoProvider({ apiKey, isDevAuthMode }),
    [apiKey, isDevAuthMode],
  )

  // Visitor/account are only meaningful once authenticated. Passing `undefined`
  // keeps the library's initializer waiting rather than identifying an
  // anonymous session — it gates on `!!visitor`.
  const visitor = useMemo<PendoVisitor | undefined>(() => {
    if (!isAuthenticated || !user) return undefined
    return {
      id: user.id,
      email: user.email,
      full_name: user.name,
      roles: roleNames,
      permissionCount: permissions.length,
    }
  }, [isAuthenticated, user, roleNames, permissions.length])

  const account = useMemo<PendoAccount | undefined>(() => {
    if (!isAuthenticated || !user) return undefined
    return {
      // Lowercased so this tenant is ONE Pendo account across AQ-Cloud2 and
      // AQ-Webcatalog. AQ-Cloud2 canonicalizes tenant GUIDs to lowercase
      // (normalizeTenantMiddleware) while the published catalog indexes use
      // uppercase; if the two apps disagree, one customer becomes two Pendo
      // accounts and every cross-app funnel silently breaks. Covered by a test.
      id: user.tenantId.toLowerCase(),
      environment: ConfigService.getEnvironment(),
      brand: ConfigService.getBrand(),
      region: ConfigService.getRegion(),
      imageTag: ConfigService.getImageTag(),
    }
  }, [isAuthenticated, user])

  return (
    <PendoProvider
      provider={provider}
      // Conditional spreads, not `prop={maybeUndefined}`: the library declares
      // these as plain `?: T` while this workspace sets
      // `exactOptionalPropertyTypes`, so passing an explicit `undefined` is a
      // type error. Omitting the prop is also what the library's own readiness
      // gates (`!!apiKey`, `!!visitor`) expect.
      {...(apiKey !== undefined ? { apiKey } : {})}
      {...(visitor !== undefined ? { visitor } : {})}
      {...(account !== undefined ? { account } : {})}
      // `enabled` still gates on the key even though ConsolePendoProvider needs
      // none: in dev-auth mode we want the console stream, so treat dev as
      // enabled regardless.
      enabled={isDevAuthMode || !!apiKey}
      deferMs={DEFER_MS}
      // MANDATORY — stops the agent capturing DOM text. AQ-Cloud2 renders
      // customer names, project names and pricing on nearly every screen, none
      // of which belongs in an analytics vendor. Do not relax.
      excludeAllText
    >
      {children}
    </PendoProvider>
  )
}
